import React, { useEffect, useState } from 'react';
import { useTheme } from '../../Providers/Themes/Themes.jsx';
import { Box, Button, Stack, Switch, TextField, Typography, useTheme as useMuiTheme } from '@mui/material';
import { Brightness4, ColorLens, Contrast, NotificationsActive, SwapHoriz } from '@mui/icons-material';
import { Storage } from '../../utils.jsx';
import {
  disableDailyReminder,
  enableDailyReminder,
  getDailyReminderStatus,
  getStoredReminderSettings,
  remindersAreSupported,
  sendTestReminder,
} from '../../utils/dailyReminder.js';

function Settings() {
  const theme = useMuiTheme();
  const storedReminder = getStoredReminderSettings();
  const [reminderEnabled, setReminderEnabled] = useState(false);
  const [reminderTime, setReminderTime] = useState(storedReminder.reminderTime);
  const [reminderBusy, setReminderBusy] = useState(false);
  const [reminderMessage, setReminderMessage] = useState('');
  const [swapKeyboardActions, setSwapKeyboardActions] = useState(() =>
    Storage.getBoolFromLocalStorage('swapKeyboardActions'),
  );
  const remindersSupported = remindersAreSupported();
  const {
    darkMode,
    toggleDarkMode,
    colorBlindMode,
    toggleColorBlindMode,
    contrastMode,
    toggleContrastMode,
  } = useTheme();

  useEffect(() => {
    getDailyReminderStatus()
      .then((settings) => {
        setReminderEnabled(settings.enabled);
        setReminderTime(settings.reminderTime);
      })
      .catch(() => setReminderEnabled(false));
  }, []);

  const handleReminderToggle = async () => {
    setReminderBusy(true);
    setReminderMessage('');
    try {
      if (reminderEnabled) {
        await disableDailyReminder();
        setReminderEnabled(false);
        setReminderMessage('Daily reminder turned off.');
      } else {
        await enableDailyReminder(reminderTime);
        setReminderEnabled(true);
        setReminderMessage('Daily reminder is ready.');
      }
    } catch (error) {
      setReminderMessage(error.message);
    } finally {
      setReminderBusy(false);
    }
  };

  const handleReminderTimeSave = async () => {
    if (!reminderEnabled) return;
    setReminderBusy(true);
    setReminderMessage('');
    try {
      await enableDailyReminder(reminderTime);
      setReminderMessage('Reminder time updated.');
    } catch (error) {
      setReminderMessage(error.message);
    } finally {
      setReminderBusy(false);
    }
  };

  const handleTestReminder = async () => {
    setReminderBusy(true);
    setReminderMessage('Sending test notification…');
    try {
      await sendTestReminder();
      setReminderMessage('Test displayed. If you don’t see it, check your system’s notification settings for this browser.');
    } catch (error) {
      setReminderMessage(error.message);
    } finally {
      setReminderBusy(false);
    }
  };

  const options = [
    {
      id: 'toggle-dark-mode',
      label: 'Dark mode',
      description: 'Use a calmer, low-light palette.',
      icon: <Brightness4 />,
      checked: darkMode,
      onChange: toggleDarkMode,
    },
    {
      id: 'toggle-color-blind-mode',
      label: 'Color blind mode',
      description: 'Use a more distinguishable color pair.',
      icon: <ColorLens />,
      checked: colorBlindMode,
      onChange: toggleColorBlindMode,
    },
    {
      id: 'toggle-swap-keyboard-actions',
      label: 'Swap Enter and Backspace buttons',
      description: 'Put Backspace on the left and Enter on the right.',
      icon: <SwapHoriz />,
      checked: swapKeyboardActions,
      onChange: () => {
        const swapped = !swapKeyboardActions;
        setSwapKeyboardActions(swapped);
        Storage.saveBoolToLocalStorage('swapKeyboardActions', swapped);
        window.dispatchEvent(new CustomEvent('keyboard-layout-change', {
          detail: { swapKeyboardActions: swapped },
        }));
      },
    },
    {
      id: 'toggle-contrast-mode',
      label: 'High contrast',
      description: 'Increase separation between game elements.',
      icon: <Contrast />,
      checked: contrastMode,
      onChange: toggleContrastMode,
    },
  ];

  return (
    <Stack spacing={1.25} sx={{ width: '100%', py: 2 }}>
      {options.map((option) => (
        <Stack
          key={option.id}
          component="label"
          htmlFor={option.id}
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{
            p: 1.5,
            borderRadius: '16px',
            cursor: 'pointer',
            border: `1px solid ${theme.custom.border}`,
            bgcolor: theme.custom.surface,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              placeItems: 'center',
              width: 42,
              height: 42,
              flexShrink: 0,
              borderRadius: '12px',
              color: theme.palette.primary.main,
              bgcolor: `${theme.palette.primary.main}18`,
            }}
          >
            {option.icon}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography fontWeight={800}>{option.label}</Typography>
            <Typography variant="body2" color="text.secondary">
              {option.description}
            </Typography>
          </Box>
          <Switch
            id={option.id}
            checked={option.checked}
            onChange={option.onChange}
            color="primary"
            inputProps={{ 'aria-label': option.label }}
          />
        </Stack>
      ))}
      <Stack
        spacing={1.25}
        sx={{
          p: 1.5,
          borderRadius: '16px',
          border: `1px solid ${theme.custom.border}`,
          bgcolor: theme.custom.surface,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              display: 'grid',
              placeItems: 'center',
              width: 42,
              height: 42,
              flexShrink: 0,
              borderRadius: '12px',
              color: theme.palette.primary.main,
              bgcolor: `${theme.palette.primary.main}18`,
            }}
          >
            <NotificationsActive />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography fontWeight={800}>Daily reminder (Beta)</Typography>
            <Typography variant="body2" color="text.secondary">
              {remindersSupported
                ? 'Get a notification if you haven’t completed today’s puzzle.'
                : 'Notifications aren’t available here. On iPhone or iPad, add Chaindle to your Home Screen first.'}
            </Typography>
          </Box>
          <Switch
            id="toggle-daily-reminder"
            checked={reminderEnabled}
            disabled={!remindersSupported || reminderBusy}
            onChange={handleReminderToggle}
            color="primary"
            inputProps={{ 'aria-label': 'Daily reminder' }}
          />
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField
            label="Reminder time"
            type="time"
            size="small"
            value={reminderTime}
            disabled={!reminderEnabled || reminderBusy}
            onChange={(event) => setReminderTime(event.target.value)}
            onBlur={handleReminderTimeSave}
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: 300 }}
            sx={{ flex: 1 }}
          />
          <Button
            variant="outlined"
            disabled={!reminderEnabled || reminderBusy}
            onClick={handleTestReminder}
          >
            Send test
          </Button>
        </Stack>

        {reminderMessage && (
          <Typography
            variant="caption"
            color={reminderMessage.includes('Could not') || reminderMessage.includes('not granted') ? 'error' : 'text.secondary'}
          >
            {reminderMessage}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}

export default Settings;
